import { type FC, type FormEvent, type HTMLAttributes, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Stack } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import JourneyDatePicker from '@Components/container/datePicker/journey/JourneyDatePicker'
import ODSearchController, {
  type ODSearchControllerProps,
} from '@Components/container/search/searchCriteria/ODSearchController'
import Button from '@DS/components/forms/button/Button'
import { getNavigationAndActionIcon } from '@DS/components/icons/navigationAndActionIcons'
import { type AutocompleteResultModel } from '@DS/components/navigation/search/atoms/AutocompleteResult'
import { useIsMediumUp } from '@Hooks/useResponsive'

import * as styles from './styles'

const ExternalLinkIcon = getNavigationAndActionIcon('external_link')

const WIDGET_ODSEARCH_FORM_NAME = 'WIDGET_ODSEARCH_FORM'

export type ODSearchFormProps = {
  isColumnDirectionForced?: boolean
  isExternalSubmitButton?: boolean
  onFocus?: VoidFunction
  onBlur?: VoidFunction
  onSubmit: (origin: AutocompleteResultModel, destination: AutocompleteResultModel, outwardDate?: Date) => void
} & Pick<
  ODSearchControllerProps,
  | 'elevation'
  | 'onError'
  | 'shouldIgnoreSuggestions'
  | 'shouldDisplayFavoritePlaces'
  | 'onClickOnAddFavoritePlace'
  | 'recalculateAutocompleteMaxHeightAfterDelayInMs'
>

const ODSearchForm: FC<
  ODSearchFormProps & Omit<HTMLAttributes<HTMLFormElement>, 'onFocus' | 'onBlur' | 'onSubmit'>
> = ({
  isExternalSubmitButton = false,
  elevation = 'onSurface',
  isColumnDirectionForced = false,
  shouldDisplayFavoritePlaces = false,
  onClickOnAddFavoritePlace,
  shouldIgnoreSuggestions = false,
  onBlur,
  onError,
  onFocus,
  onSubmit,
  recalculateAutocompleteMaxHeightAfterDelayInMs = 0,
  ...rest
}) => {
  const theme = useTheme()
  const { formatMessage } = useIntl()
  const isMediumUp = useIsMediumUp(theme)

  const originRef = useRef<HTMLInputElement>(undefined)
  const destinationRef = useRef<HTMLInputElement>(undefined)

  const [origin, setOrigin] = useState<AutocompleteResultModel>()
  const [destination, setDestination] = useState<AutocompleteResultModel>()
  const [outwardDate, setOutwardDate] = useState<Date>(new Date())
  const [shouldDisplayError, setShouldDisplayError] = useState(false)

  const stackDirection = isColumnDirectionForced || !isMediumUp ? 'column' : 'row'
  const isRowDirection = stackDirection === 'row'

  const onSelectOrigin = (selectedOrigin: AutocompleteResultModel) => {
    setOrigin(selectedOrigin)

    if (!destination) {
      destinationRef.current?.focus()
    }
  }

  const onSelectDestination = (selectedDestination: AutocompleteResultModel) => {
    setDestination(selectedDestination)

    if (!origin) {
      originRef.current?.focus()
    }
  }

  const swapOD = () => {
    setOrigin(destination)
    setDestination(origin)
  }

  const onDateChange = (outwardDate: Date) => {
    onFocus?.()
    setOutwardDate(outwardDate)
  }

  const onSubmitForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setShouldDisplayError(true)

    if (!origin || !destination || origin.id === destination.id) {
      return
    }

    onSubmit(origin, destination, outwardDate)
  }

  const errorText = useMemo(() => {
    if (!shouldDisplayError) {
      return undefined
    }

    if (!origin) {
      if (!destination) {
        return formatMessage({ id: 'odSearch_origin_and_destination_required' })
      }

      return formatMessage({ id: 'odSearch_origin_required' })
    }

    if (!destination) {
      return formatMessage({ id: 'odSearch_destination_required' })
    }

    if (origin.id === destination.id) {
      return formatMessage({ id: 'odSearch_identical_origin_destination' })
    }

    return undefined
  }, [origin, destination, formatMessage, shouldDisplayError])

  useEffect(() => {
    setShouldDisplayError(false)
  }, [origin, destination])

  return (
    <form
      {...rest}
      onSubmit={onSubmitForm}
      name={WIDGET_ODSEARCH_FORM_NAME}
      css={styles.odSearchForm(theme)}
      noValidate
    >
      <Stack direction={stackDirection} gap={2} alignItems="center" justifyContent="center" width="100%">
        <ODSearchController
          css={styles.odSearch}
          isSticky={isRowDirection}
          elevation={elevation}
          recalculateAutocompleteMaxHeightAfterDelayInMs={recalculateAutocompleteMaxHeightAfterDelayInMs}
          originAutocompleteProps={{
            onSelectItem: onSelectOrigin,
          }}
          destinationAutocompleteProps={{
            onSelectItem: onSelectDestination,
          }}
          originInputPlaceProps={{
            inputRef: originRef,
            inputProps: { 'aria-invalid': !origin },
            required: true,
            onFocus,
            onBlur,
          }}
          destinationInputPlaceProps={{
            inputRef: destinationRef,
            inputProps: { 'aria-invalid': !destination },
            required: true,
            onFocus,
            onBlur,
          }}
          onSwap={swapOD}
          onClearOrigin={() => setOrigin(undefined)}
          onClearDestination={() => setDestination(undefined)}
          onError={onError}
          hasAutoFocusDisabled
          shouldDisplayFavoritePlaces={shouldDisplayFavoritePlaces}
          onClickOnAddFavoritePlace={onClickOnAddFavoritePlace}
          shouldIgnoreSuggestions={shouldIgnoreSuggestions}
          hasAutocompleteWithBorder
          hasToRestoreSelectedOnBlur
          errorHelperText={errorText}
        />
        <JourneyDatePicker
          css={styles.datepicker(isRowDirection && !!errorText)}
          initStartDate={{
            date: outwardDate,
            inputDateLabel: `${formatMessage({ id: 'date' })} ${formatMessage({ id: 'dateFormat' })}`,
            buttonLabelId: 'dateLabelWithDate',
            isDateMandatory: true,
          }}
          isElevated={elevation === 'onSurface'}
          mode="SINGLE_START_DATE_WITH_HOURS"
          size="small"
          onClick={onFocus}
          onDateChange={onDateChange}
        />
      </Stack>
      <Button
        type="submit"
        isExternal={isExternalSubmitButton}
        icon={isExternalSubmitButton ? ExternalLinkIcon : undefined}
      >
        <FormattedMessage id="odSearch_submitLabel" />
      </Button>
    </form>
  )
}

export default ODSearchForm
